/**
 * Voucher Redemption Locator — PUBLIC page at /voucher-locations (no login).
 *
 * BRD "Voucher Redemption Locator Requirements 09/02/2026": linked from the voucher email as
 * /voucher-locations?code={{voucher_code}} (generate-offer already sends voucher_code to the SendGrid
 * template, so adding the link is a template edit). With a code, results are ONLY partners that can
 * redeem that voucher; without one, every listed partner. All of that is decided server-side by
 * voucher_locator_search via the `voucher-locator` edge function — this page only renders.
 *
 * Cards show name, address, phone and a Website button — never "Sign Up" or "Shooting qualification
 * available" (BRD 5.4). Order is shuffled within distance bands by the server (BRD 5.3.3).
 *
 * Look (marketing revisions 2026-10-14): wrapped in a static replica of the ww2.uslawshield.com
 * header/footer (Ww2SiteChrome.tsx); hero copy/type, colours and markers per the sticky notes — see
 * VoucherLocator.css. Behaviour is unchanged.
 *
 * Business feedback 2026-10-22: (1) the first search of a browser session opens a "Vouchers must be
 * redeemed in person" notice (InPersonNotice.tsx; dismissal remembered in sessionStorage) and the same
 * message stays as a one-line note under the Search button; (2) Use My Location — the button or the
 * crosshair inside the ZIP field — fills the ZIP field with the ZIP nearest the member's coordinates
 * (`near_zip`, derived server-side from us_zip_codes) and the results say "near <ZIP>". Location is
 * only ever requested on a click, never on page load.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import InPersonNotice from "../components/voucherLocator/InPersonNotice";
import LocatorMap from "../components/voucherLocator/LocatorMap";
import {
  Ww2Footer,
  Ww2Header,
} from "../components/voucherLocator/Ww2SiteChrome";
import { searchVoucherLocations } from "../services/voucherLocator.service";
import {
  IN_PERSON_NOTICE,
  LOCATOR_RADII,
  addressLine,
  autoSearchHint,
  autoSearchOutcome,
  inPersonNoticeDismissed,
  isValidZip,
  locationErrorMessage,
  locationSearchHint,
  nearZipFromResponse,
  rememberInPersonNoticeDismissed,
  safeWebsite,
  statusMessage,
  voucherCodeFromSearch,
  type LocatorRadius,
  type LocatorResponse,
} from "../utils/voucherLocator";
import "./VoucherLocator.css";

const VoucherLocator: React.FC = () => {
  const location = useLocation();
  const code = useMemo(
    () => voucherCodeFromSearch(location.search),
    [location.search],
  );

  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState<LocatorRadius>(25);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<LocatorResponse | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [activeN, setActiveN] = useState<number | null>(null);
  const [locating, setLocating] = useState(false);
  // "Locations near 77007 (your current location)" after a location search; cleared by any other search.
  const [locationZip, setLocationZip] = useState<string | null>(null);
  const [showNotice, setShowNotice] = useState(false);
  const zipRef = useRef<HTMLInputElement>(null);

  // The shared index.html carries the CRM's internal title; this public page (also served on
  // locator.uslawshield.com) shows its own.
  useEffect(() => {
    const previous = document.title;
    document.title = "Find a Participating Partner | U.S. LawShield";
    return () => {
      document.title = previous;
    };
  }, []);

  // Arrival search (owner 2026-09-29): opened from the voucher email, search right away around the
  // voucher holder's ZIP. The server resolves the ZIP (the browser never reads the contact) and says
  // "no_holder_zip" when there is none — then the page simply waits, as before. Anything the member
  // does first (a typed ZIP, Use My Location) wins and a late arrival result is ignored.
  const [autoZip, setAutoZip] = useState<string | null>(null);
  const userSearched = useRef(false);
  useEffect(() => {
    if (!code) {
      return;
    }
    let cancelled = false;
    setBusy(true);
    searchVoucherLocations({ code, radius: 25, auto: true })
      .then((resp) => {
        if (cancelled || userSearched.current) {
          return;
        }
        const outcome = autoSearchOutcome(resp);
        if (!outcome.apply) {
          return;
        }
        if (outcome.zip) {
          setZip(outcome.zip);
          setAutoZip(outcome.zip);
        }
        setResult(resp);
        // The arrival search (from the voucher email) also counts as the first search of the session.
        if (!inPersonNoticeDismissed()) {
          setShowNotice(true);
        }
      })
      .catch(() => {
        /* arrival search is best-effort; the member can still search */
      })
      .finally(() => {
        if (!cancelled && !userSearched.current) {
          setBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  // First search of the session (member-run, or the arrival search above): show the in-person notice
  // alongside the results.
  const maybeShowNotice = () => {
    if (!inPersonNoticeDismissed()) {
      setShowNotice(true);
    }
  };
  const closeNotice = () => {
    rememberInPersonNoticeDismissed();
    setShowNotice(false);
  };

  const run = async (point: { zip: string } | { lat: number; lng: number }) => {
    userSearched.current = true;
    setAutoZip(null);
    setLocationZip(null);
    setBusy(true);
    setInputError(null);
    setActiveN(null);
    maybeShowNotice();
    try {
      const resp = await searchVoucherLocations({ code, radius, ...point });
      if ("lat" in point) {
        // fill the field with the ZIP for these coordinates (empty if the server couldn't derive one)
        const near = nearZipFromResponse(resp);
        setZip(near ?? "");
        setLocationZip(resp.status === "ok" ? near : null);
      }
      setResult(resp);
    } finally {
      setBusy(false);
    }
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidZip(zip)) {
      setInputError("Enter a 5-digit ZIP code, or use your location.");
      zipRef.current?.focus();
      return;
    }
    void run({ zip: zip.trim() });
  };

  // Asked only on a click (the button or the crosshair in the ZIP field). The coordinates go to the
  // search request and nowhere else: not stored, not logged, not kept in state.
  const onUseLocation = () => {
    if (!("geolocation" in navigator) || !navigator.geolocation) {
      setInputError(locationErrorMessage(null));
      return;
    }
    setInputError(null);
    setBusy(true);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        void run({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        setLocating(false);
        setBusy(false);
        setInputError(locationErrorMessage(err?.code ?? 2));
        zipRef.current?.focus(); // manual entry keeps working: put the member back in the field
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  // memoised: a fresh [] each render would rebuild the map pins on every keystroke
  const locations = useMemo(
    () => (result?.status === "ok" ? (result.locations ?? []) : []),
    [result],
  );
  const message = result ? statusMessage(result.status) : null;
  const total = result?.total ?? 0;

  return (
    <div className="vl-page">
      <Ww2Header />
      <section className="vl-banner">
        <h1>Find a Participating Partner Near You.</h1>
        <p>
          Your U.S. LawShield&reg; voucher gives you access to exclusive
          benefits at participating retail partners. Find a location near you to
          redeem your voucher. Simply enter your ZIP code or use your current
          location to explore participating partners in your area.
        </p>
      </section>

      <main className="vl-layout">
        <section className="vl-search-area" aria-label="Search">
          <form className="vl-search" onSubmit={onSearch} noValidate>
            <h2>Search</h2>
            <label className="vl-label" htmlFor="vl-zip">
              ZIP CODE
            </label>
            <div className="vl-zip-wrap">
              <input
                id="vl-zip"
                ref={zipRef}
                className="vl-field"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={10}
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="e.g. 77007"
                aria-describedby={inputError ? "vl-input-error" : undefined}
              />
              <button
                type="button"
                className="vl-zip-locate"
                onClick={onUseLocation}
                disabled={busy}
                aria-label="Use my location to fill in my ZIP code"
                title="Use my location"
                data-testid="voucher-locator-locate-icon"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="12" r="2.5" fill="currentColor" />
                  <path
                    d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            {/* right under the field it is about, so it is on screen when it appears */}
            {inputError && (
              <p
                className="vl-alert vl-alert-inline"
                role="alert"
                id="vl-input-error"
              >
                {inputError}
              </p>
            )}
            <label className="vl-label" htmlFor="vl-radius">
              DISTANCE
            </label>
            <select
              id="vl-radius"
              className="vl-field"
              value={radius}
              onChange={(e) =>
                setRadius(Number(e.target.value) as LocatorRadius)
              }
            >
              {LOCATOR_RADII.map((r) => (
                <option key={r} value={r}>
                  {r} Miles
                </option>
              ))}
            </select>
            <button
              type="button"
              className="vl-btn vl-btn-outline"
              onClick={onUseLocation}
              disabled={busy}
            >
              {locating ? "Finding your location…" : "Use My Location"}
            </button>
            <button
              type="submit"
              className="vl-btn vl-btn-maroon"
              disabled={busy}
            >
              {busy ? "Searching…" : "Search"}
            </button>
            <p
              className="vl-in-person-note"
              data-testid="voucher-locator-in-person-note"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                aria-hidden="true"
                focusable="false"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 7v6M12 16.5v.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span>{IN_PERSON_NOTICE.note}</span>
            </p>
          </form>
          {message && (
            <p className="vl-alert" role="alert">
              {message}
            </p>
          )}
        </section>

        <section className="vl-results" aria-label="Results" aria-live="polite">
          {autoZip && result?.status === "ok" && (
            <p className="vl-hint" data-testid="voucher-locator-auto-hint">
              {autoSearchHint(autoZip)}
            </p>
          )}
          {locationZip && result?.status === "ok" && (
            <p className="vl-hint" data-testid="voucher-locator-location-hint">
              {locationSearchHint(locationZip, Boolean(code))}
            </p>
          )}
          {result?.status === "ok" && (
            <div className="vl-count">
              {total === 0
                ? `No locations found within ${result.radius_miles} miles. Try a larger distance.`
                : total > locations.length
                  ? `Showing the ${locations.length} nearest of ${total} Locations Found`
                  : `${total} Location${total === 1 ? "" : "s"} Found`}
            </div>
          )}
          {locations.map((loc) => {
            const website = safeWebsite(loc.website);
            return (
              <article
                key={loc.n}
                className={`vl-card${activeN === loc.n ? " active" : ""}`}
                onClick={() => setActiveN(loc.n)}
                data-testid="voucher-locator-card"
              >
                <h3>
                  <span className="vl-pin-num">{loc.n}</span> {loc.name}
                </h3>
                <p className="vl-meta">
                  {loc.type} · {loc.miles} mi
                </p>
                <p className="vl-addr">{addressLine(loc)}</p>
                {loc.phone && (
                  <p className="vl-phone">
                    <a href={`tel:${loc.phone.replace(/[^\d+]/g, "")}`}>
                      {loc.phone}
                    </a>
                  </p>
                )}
                {website && (
                  <a
                    className="vl-website"
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Website
                  </a>
                )}
              </article>
            );
          })}
        </section>

        <section className="vl-map-area" aria-label="Map">
          <LocatorMap
            locations={locations}
            center={result?.status === "ok" ? (result.center ?? null) : null}
            activeN={activeN}
            onSelect={setActiveN}
          />
        </section>
      </main>
      <Ww2Footer />
      {showNotice && <InPersonNotice onClose={closeNotice} />}
    </div>
  );
};

export default VoucherLocator;
